import { SUPPORTED_ACTIONS } from './constants'
import { EMPTY_STATE, canShow } from './repeatPolicy'
import type { ModalRule, ModalTrigger, RepeatState, TriggerContext } from './types'

// Пустой или отсутствующий список не сужает.
function includesOrEmpty (list: number[] | undefined, value: number | undefined): boolean {
  if (!list || list.length === 0) return true
  return value !== undefined && list.includes(value)
}

// Проверяем, что payload парсится как абсолютный URL с http(s) протоколом.
function isValidHttpUrl (urlString: string): boolean {
  try {
    const url = new URL(urlString)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function matchesContext (rule: ModalRule, ctx: TriggerContext): boolean {
  const c = rule.context
  if (!c) return true
  switch (rule.trigger) {
    case 'web_course_view_enter':
      return includesOrEmpty(c.courseIds, ctx.courseId)
    case 'web_course_day_enter':
      return includesOrEmpty(c.courseIds, ctx.courseId) && includesOrEmpty(c.dayIds, ctx.dayId)
    default:
      return true
  }
}

export function isInPeriod (rule: ModalRule, now: number): boolean {
  if (rule.startAt && now < rule.startAt.getTime()) return false
  if (rule.endAt && now > rule.endAt.getTime()) return false
  return true
}

// Незнакомое действие или битая ссылка — модалку не показываем, кнопку-пустышку не рисуем.
export function hasSupportedActions (rule: ModalRule, isKnownRoute: (path: string) => boolean): boolean {
  return rule.content.buttons.every(b => {
    if (!SUPPORTED_ACTIONS.includes(b.action)) return false
    if (b.action === 'close') return true
    if (!b.payload) return false
    if (b.action === 'url' || b.action === 'webview') {
      return isValidHttpUrl(b.payload)
    }
    return b.action !== 'deeplink' || isKnownRoute(b.payload)
  })
}

export function selectRule (
  rules: ModalRule[],
  trigger: ModalTrigger,
  ctx: TriggerContext,
  states: Map<number, RepeatState>,
  now: number,
  isKnownRoute: (path: string) => boolean
): ModalRule | null {
  return rules.find(rule =>
    rule.trigger === trigger &&
    matchesContext(rule, ctx) &&
    isInPeriod(rule, now) &&
    hasSupportedActions(rule, isKnownRoute) &&
    canShow(rule.repeatPolicy, states.get(rule.modalId) ?? EMPTY_STATE, now)
  ) ?? null
}
